import {
  useQuery,
  useQueryClient,
  useMutation,
  QueryClient,
  InvalidateQueryFilters,
} from "@tanstack/react-query";
import { QUERY_NAME, SERVICES } from "@lhd/constants";
import { handleMessageError } from "@lhd/features/Apps/utils";
import axios from "@lhd/libs/axios";
import { getCompanyId, getUid, getAccessToken } from "@lhd/utility";
import { notification } from "antd";
import { isFunction } from "lodash";
import moment from "moment";
import type { RepetitionMeetingApi } from "../constants/meeting";
import { ReportForm, ReportTask } from "types/projectManager/meeting";
import { normalizeFilterParams } from "../utils/filterParamsNormalizer";

let invalidateQueriesTimeout: NodeJS.Timeout | null = null;
const debouncedInvalidateQueries = (
  queryClient: QueryClient,
  queryKey: any[],
  delay = 100,
  refetchType: InvalidateQueryFilters["refetchType"] = undefined,
) => {
  if (invalidateQueriesTimeout) {
    clearTimeout(invalidateQueriesTimeout);
  }
  invalidateQueriesTimeout = setTimeout(() => {
    const matchingQueries = queryClient.getQueryCache().findAll(queryKey);

    queryClient.invalidateQueries({
      queryKey,
      refetchType,
    });

    const activeQueries = matchingQueries.filter((query: any) => {
      return query.getObserversCount() > 0;
    });

    if (activeQueries.length > 0) {
      queryClient.refetchQueries({
        queryKey: activeQueries[0].queryKey,
        exact: true,
      });
    }

    invalidateQueriesTimeout = null;
  }, delay);
};

export const useFetchBizProject = (
  projectId?: string,
  filterParams?: {
    category_status_company_ids?: string[];
    name?: string;
    page?: number;
    per_page?: number;
    task_status?: string[];
    task_manager?: string[];
    /** When true, include tasks with no assignee (replaces __NULL__ in task_manager). */
    include_unassigned?: boolean;
    filters?: Record<string, string[]>;
    search?: Record<string, string>;
    sort?: Record<string, "asc" | "desc">;
    __BLOCK_API__?: boolean;
  },
) => {
  const uid = getUid();
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.BIZ_PROJECT, uid, projectId, filterParams],
    () =>
      axios({
        method: "get",
        url: projectId
          ? `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}`
          : SERVICES.BIZ_PROJECT_MANAGER,
        params: {
          login_company_id: companyId,
          ...(filterParams?.category_status_company_ids?.length
            ? {
                category_status_company_ids:
                  filterParams.category_status_company_ids,
              }
            : {}),
          name: filterParams?.name,
          page: filterParams?.page,
          per_page: filterParams?.per_page,
          ...(filterParams?.task_status?.length
            ? { task_status: filterParams.task_status }
            : {}),
          ...(filterParams?.task_manager?.length
            ? { task_manager: filterParams.task_manager }
            : {}),
          ...(filterParams?.include_unassigned
            ? { include_unassigned: true }
            : {}),
          ...(filterParams?.filters
            ? normalizeFilterParams({ filters: filterParams.filters })
            : {}),
          ...(filterParams?.search ? { search: filterParams.search } : {}),
          ...(filterParams?.sort ? { sort: filterParams.sort } : {}),
        },
      }).then((res) => {
        return res.data as any;
      }),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      // Keep the previous query's data visible while a new query (e.g. after filter change)
      // is in-flight. Without this, data briefly becomes undefined → detailGanttPolicy → null
      // → PageHeader buttons flicker disabled/enabled during each filter refetch.
      keepPreviousData: true,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled:
        !!getAccessToken() &&
        (!!projectId || !!filterParams) &&
        !filterParams?.__BLOCK_API__,
    },
  );
};

export const useFetchProjectFilterOptions = (
  payload: {
    field_names: string[];
    active_filters: Record<string, string[]>;
  } | null,
  enabled: boolean,
) => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.BIZ_PROJECT, "filter_options", payload],
    () =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/filter_options`,
        data: {
          login_company_id: companyId,
          ...payload,
        },
      }).then((res) => res.data as any),
    {
      cacheTime: 60000,
      staleTime: 60000,
      enabled: enabled && !!payload,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
    },
  );
};

export const useFetchGanttAssignees = (
  categoryStatusCompanyIds?: string[],
  enabled = true,
) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const statusIds = categoryStatusCompanyIds ?? [];

  return useQuery(
    [QUERY_NAME.BIZ_PROJECT_ASSIGNEES, uid, companyId, statusIds],
    () =>
      axios({
        method: "get",
        url: SERVICES.BIZ_PROJECT_LIST_USER_ASSIGNEES,
        params: {
          login_company_id: companyId,
          category_status_company_ids: statusIds,
        },
      }).then((res) => res.data as any),
    {
      cacheTime: 60000,
      staleTime: 0,
      refetchOnMount: "always",
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: enabled && !!getAccessToken() && statusIds.length > 0,
    },
  );
};

export const useFetchGanttProjectAssignees = (projectId?: string) => {
  const uid = getUid();

  return useQuery(
    [QUERY_NAME.BIZ_PROJECT_ASSIGNEES, uid, projectId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/list_user_assignees`,
      }).then((res) => res.data as any),
    {
      cacheTime: 60000,
      staleTime: 0,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!getAccessToken() && !!projectId,
    },
  );
};

export const useFetchBizProjectTaskDetail = (taskId?: string) => {
  const uid = getUid();
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.BIZ_PROJECT_TASK, uid, taskId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/${taskId}`,
        params: {
          login_company_id: companyId,
        },
      }).then((res) => {
        return res.data as any;
      }),
    {
      cacheTime: 3000,
      staleTime: 3000,
      refetchOnMount: "always",
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!taskId,
    },
  );
};

export const useAddProject = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers }: any) =>
      axios({
        method: payload?.id ? "put" : "post",
        url: payload?.id
          ? `${SERVICES.BIZ_PROJECT_MANAGER}/${payload?.id}`
          : SERVICES.BIZ_PROJECT_MANAGER,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const {
          invalidateQueries = true,
          showNotification = true,
          onSuccess: onSuccessCallback,
        } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        if (showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        // Call onSuccess from variables if provided, otherwise use hook's onSuccess
        const callback = onSuccessCallback || onSuccess;
        callback?.(data, variables);
      },
      onError(e: any) {
        const duplicateNameErrorMessageAPIResponse =
          "Nameこのプロジェクト名は既に使用されています。別の名前を入力してください。";
        const duplicateNameErrorMessage =
          "このプロジェクト名は既に使用されています。別の名前を入力してください。";
        if (e?.response?.data?.error === duplicateNameErrorMessageAPIResponse) {
          notification["error"]({
            message: duplicateNameErrorMessage,
          });
        } else {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.error),
          });
        }
      },
    },
  );
};

export const useAddProjectPhase = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers }: any) =>
      axios({
        method: payload?.id ? "put" : "post",
        url: payload?.id
          ? `${SERVICES.BIZ_PROJECT_MANAGER_PHASE}/${payload?.id}`
          : SERVICES.BIZ_PROJECT_MANAGER_PHASE,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true, showNotification = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        if (showNotification) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

/**
 * Hook to create phases with nested tasks in a single API call
 * Used when user creates new phase(s) with child tasks at the same time
 */
export const useCreatePhasesWithTasks = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({
      payload,
      showNotification = true,
    }: {
      payload: any;
      showNotification?: boolean;
    }) =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/create_phases_with_tasks`,
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useAddProjectTask = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers }: any) => {
      // Use JSON if children_attributes is present, otherwise use FormData
      if (payload.children_attributes?.length) {
        return axios({
          method: payload?.id ? "put" : "post",
          url: payload?.id
            ? `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/${payload?.id}`
            : SERVICES.BIZ_PROJECT_MANAGER_TASK,
          data: payload,
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
        });
      }

      const formData = new FormData();

      if (payload.project_phase_id)
        formData.append("project_phase_id", payload.project_phase_id);
      if (payload.parent_id !== undefined) {
        formData.append(
          "parent_id",
          payload.parent_id === null ? "" : payload.parent_id,
        );
      }
      if (payload.name) formData.append("name", payload.name);
      if (payload.manager) formData.append("manager", payload.manager);
      if (payload.start_date) formData.append("start_date", payload.start_date);
      if (payload.end_date) formData.append("end_date", payload.end_date);
      if (payload.completed_date)
        formData.append("completed_date", payload.completed_date);
      if (payload.estimate_hours)
        formData.append("estimate_hours", payload.estimate_hours);
      if (payload.project_task_status_id)
        formData.append(
          "project_task_status_id",
          payload.project_task_status_id,
        );
      if (payload.note) formData.append("note", payload.note);
      if (payload.url) formData.append("url", payload.url);
      if (payload.description)
        formData.append("description", payload.description);
      if (payload.sort_order) formData.append("sort_order", payload.sort_order);
      if (payload.sort_order_position !== undefined)
        formData.append(
          "sort_order_position",
          String(payload.sort_order_position),
        );
      if (payload.temp_id) formData.append("temp_id", payload.temp_id);

      if (payload.attachments?.length) {
        payload.attachments.forEach((file: any) => {
          formData.append("attachments[]", file.originFileObj || file);
        });
      }

      return axios({
        method: payload?.id ? "put" : "post",
        url: payload?.id
          ? `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/${payload?.id}`
          : SERVICES.BIZ_PROJECT_MANAGER_TASK,
        data: formData,
        headers: headers,
      });
    },
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true, showNotification = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT_TASK,
            uid,
          ]);
          debouncedInvalidateQueries(queryClient, [QUERY_NAME.SETTING_MEMBERS]);
        }
        if (showNotification) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

const getDeleteUrl = (id: string | number, type: string) => {
  let url = SERVICES.BIZ_PROJECT_MANAGER;
  if (type === "phase") {
    url = SERVICES.BIZ_PROJECT_MANAGER_PHASE;
  }
  if (type === "task") {
    url = SERVICES.BIZ_PROJECT_MANAGER_TASK;
  }
  return `${url}/${id}`;
};
export const useDeleteProjectPhaseTask = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({ id, type }: any) =>
      axios({
        method: "delete",
        url: getDeleteUrl(id, type),
      }),
    {
      onSuccess() {
        onSuccess?.();
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteMultipleProjects = ({
  onSuccessHandler,
}: {
  onSuccessHandler?: () => void;
}) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  const uid = getUid();

  return useMutation(
    ({
      listProjects,
      showNotification = true,
    }: {
      listProjects: React.Key[];
      showNotification?: boolean;
    }) => {
      const formData = new FormData();
      listProjects.forEach((id) => {
        formData.append("project_ids[]", String(id));
      });
      formData.append("login_company_id", companyId);
      return axios({
        method: "delete",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/bulk_delete`,
        data: formData,
      });
    },
    {
      onSuccess(e, variables) {
        isFunction(onSuccessHandler) && onSuccessHandler();
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e: any, variables) {
        if (variables.showNotification !== false) {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.msg),
          });
        }
      },
    },
  );
};

export const useBulkDeleteProjectPhases = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({
      phaseIds,
      showNotification = true,
    }: {
      phaseIds: number[];
      showNotification?: boolean;
    }) => {
      return axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_PHASE}/bulk_delete`,
        data: { project_phase_ids: phaseIds },
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification.success({ message: "削除しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useBulkDeleteProjectTasks = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({
      taskIds,
      showNotification = true,
    }: {
      taskIds: number[];
      showNotification?: boolean;
    }) => {
      return axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/bulk_delete`,
        data: { project_task_ids: taskIds },
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification.success({ message: "削除しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useCopyProject = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, method, headers, showNotification = true }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/bulk_copy`,
        data: { ...payload, login_company_id: companyId },
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        const errorMsg = e?.response?.data?.error;
        notification["error"]({
          message: handleMessageError(errorMsg),
        });
      },
    },
  );
};

export const useCopyProjectPhase = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers, showNotification = true }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_PHASE}/bulk_copy`,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useCopyProjectTask = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers, showNotification = true }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/bulk_copy`,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        // Only show notification if showNotification is explicitly true (not false or undefined)
        if (variables.showNotification === true) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useReorderProjectPhase = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ phaseId, sortOrder, headers }: any) =>
      axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_PHASE}/${phaseId}/reorder`,
        params: { sort_order_position: sortOrder },
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useReorderProjectTask = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ taskId, sortOrder, headers }: any) =>
      axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER_TASK}/${taskId}/reorder`,
        params: { sort_order_position: sortOrder },
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useReorderProject = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, sortOrder, headers }: any) =>
      axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/reorder`,
        params: { sort_order_position: sortOrder },
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useReorderAllProjects = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, headers, showNotification = true }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/reorder_all`,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        const { invalidateQueries = true } = variables;
        if (invalidateQueries) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
          ]);
        }
        if (variables.showNotification === true) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateProject = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ payload, method, headers, showNotification = true }: any) =>
      axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/bulk_update`,
        data: payload,
        headers,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        if (variables.showNotification === true) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        const ERROR_TEXT = "End datemust be on or after start_date";
        if (e?.response?.data?.errors) {
          const hasEndDateError = e?.response?.data?.errors.some(
            (msg: string) => msg.includes(ERROR_TEXT),
          );
          if (hasEndDateError) {
            const displayMessage =
              "終了時間は開始時間より後の日付を選択してください";
            notification["error"]({
              message: handleMessageError(displayMessage),
            });
          }
        } else {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.msg),
          });
        }
      },
    },
  );
};

// ============================================
// Project Phase Status Hooks
// ============================================

type PhaseStatusPayload = {
  projectId: number | string;
  name: string;
  key: string;
  position?: number;
};

type PhaseStatusUpdatePayload = {
  projectId: number | string;
  statusId: number | string;
  name?: string;
  key?: string;
  position?: number;
};

type PhaseStatusDeletePayload = {
  projectId: number | string;
  statusId: number | string;
};

export const useCreateProjectPhaseStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, name, key, position }: PhaseStatusPayload) => {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("key", key);
      if (position !== undefined) {
        formData.append("position", String(position));
      }

      return axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_phase_statuses`,
        data: formData,
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateProjectPhaseStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({
      projectId,
      statusId,
      name,
      key,
      position,
    }: PhaseStatusUpdatePayload) => {
      const formData = new FormData();
      if (name !== undefined) formData.append("name", name);
      if (key !== undefined) formData.append("key", key);
      if (position !== undefined) formData.append("position", String(position));

      return axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_phase_statuses/${statusId}`,
        data: formData,
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteProjectPhaseStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, statusId }: PhaseStatusDeletePayload) =>
      axios({
        method: "delete",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_phase_statuses/${statusId}`,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "削除しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

// ============================================
// Project Task Status Hooks
// ============================================

type TaskStatusPayload = {
  projectId: number | string;
  name: string;
  key: string;
  position?: number;
};

type TaskStatusUpdatePayload = {
  projectId: number | string;
  statusId: number | string;
  name?: string;
  key?: string;
  position?: number;
};

type TaskStatusDeletePayload = {
  projectId: number | string;
  statusId: number | string;
};

export const useCreateProjectTaskStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, name, key, position }: TaskStatusPayload) => {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("key", key);
      if (position !== undefined) {
        formData.append("position", String(position));
      }

      return axios({
        method: "post",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_task_statuses`,
        data: formData,
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateProjectTaskStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, statusId, name, key, position }: TaskStatusUpdatePayload) => {
      const formData = new FormData();
      if (name !== undefined) formData.append("name", name);
      if (key !== undefined) formData.append("key", key);
      if (position !== undefined) formData.append("position", String(position));

      return axios({
        method: "put",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_task_statuses/${statusId}`,
        data: formData,
      });
    },
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteProjectTaskStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({ projectId, statusId }: TaskStatusDeletePayload) =>
      axios({
        method: "delete",
        url: `${SERVICES.BIZ_PROJECT_MANAGER}/${projectId}/project_task_statuses/${statusId}`,
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "削除しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export type CreateBizMeetingPayload = {
  biz_project_id: number;
  name: string;
  time_meeting: string;
  worker_ids: number[];
  repetition_meeting: RepetitionMeetingApi;
  day_of_week?: string;
  week_of_month?: string;
  custom_interval?: number;
  custom_unit?: string;
  report_form?: boolean;
  report_days_before?: string;
  report_hours?: string;
  report_delayed_tasks?: boolean;
  report_in_progress_tasks?: boolean;
  report_new_tasks?: boolean;
  report_new_tasks_target_period?: number | null;
  report_pic_announcement?: boolean;
};

export type UpdateBizMeetingPayload = Omit<
  CreateBizMeetingPayload,
  "biz_project_id" | "time_meeting"
> & {
  /** Omit on PUT when 開始日時 is frozen in edit (server keeps existing `time_meeting`). */
  time_meeting?: string;
};

function buildBizMeetingFormData(
  payload: CreateBizMeetingPayload | UpdateBizMeetingPayload,
  opts: { includeBizProjectId: boolean },
): FormData {
  const formData = new FormData();
  if (opts.includeBizProjectId) {
    const p = payload as CreateBizMeetingPayload;
    formData.append("biz_project_id", String(p.biz_project_id));
  }
  formData.append("name", payload.name);
  if (opts.includeBizProjectId) {
    formData.append(
      "time_meeting",
      (payload as CreateBizMeetingPayload).time_meeting,
    );
  } else {
    const p = payload as UpdateBizMeetingPayload;
    if (p.time_meeting !== undefined && p.time_meeting !== "") {
      formData.append("time_meeting", p.time_meeting);
    }
  }
  formData.append("repetition_meeting", payload.repetition_meeting);

  payload.worker_ids.forEach((id) => {
    formData.append("worker_ids[]", String(id));
  });

  if (payload.day_of_week) {
    formData.append("day_of_week", payload.day_of_week);
  }
  if (payload.week_of_month)
    formData.append("week_of_month", payload.week_of_month);
  if (payload.custom_interval != null)
    formData.append("custom_interval", String(payload.custom_interval));
  if (payload.custom_unit) {
    formData.append("custom_unit", payload.custom_unit);
  }
  if (payload.report_form != null)
    formData.append("report_form", String(payload.report_form));
  if (payload.report_days_before)
    formData.append("report_days_before", payload.report_days_before);
  if (payload.report_hours) {
    formData.append("report_hours", payload.report_hours);
  }
  if (payload.report_delayed_tasks != null)
    formData.append(
      "report_delayed_tasks",
      String(payload.report_delayed_tasks),
    );
  if (payload.report_in_progress_tasks != null)
    formData.append(
      "report_in_progress_tasks",
      String(payload.report_in_progress_tasks),
    );
  if (payload.report_new_tasks != null)
    formData.append("report_new_tasks", String(payload.report_new_tasks));
  if (payload.report_new_tasks_target_period !== undefined)
    formData.append(
      "report_new_tasks_target_period",
      payload.report_new_tasks_target_period === null
        ? ""
        : String(payload.report_new_tasks_target_period),
    );
  if (payload.report_pic_announcement != null)
    formData.append(
      "report_pic_announcement",
      String(payload.report_pic_announcement),
    );

  return formData;
}

/** Extract meeting id from POST /biz_meetings response (handles `data` or `data.id` wrappers). */
export function getBizMeetingIdFromCreateResponse(
  res: { data?: unknown } | null | undefined,
): number | undefined {
  if (res?.data == null) return undefined;
  const d = res.data as Record<string, unknown>;
  if (typeof d !== "object" || d === null) return undefined;
  if (typeof d.id === "number" && Number.isFinite(d.id)) return d.id;
  if (typeof d.id === "string") {
    const n = Number(d.id);
    if (Number.isFinite(n)) return n;
  }
  const inner = d.data;
  if (inner && typeof inner === "object" && "id" in (inner as object)) {
    const id = (inner as { id?: unknown }).id;
    if (typeof id === "number" && Number.isFinite(id)) return id;
    if (typeof id === "string") {
      const n = Number(id);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

export const useCreateBizMeeting = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  const companyId = getCompanyId();

  return useMutation(
    (payload: CreateBizMeetingPayload) => {
      return axios({
        method: "post",
        url: SERVICES.BIZ_MEETINGS,
        data: buildBizMeetingFormData(payload, { includeBizProjectId: true }),
      });
    },
    {
      onSuccess: (data, variables) => {
        const projectId = Number(
          (variables as CreateBizMeetingPayload).biz_project_id,
        );
        if (Number.isFinite(projectId)) {
          queryClient.invalidateQueries({
            queryKey: [SERVICES.BIZ_MEETINGS, uid, companyId, projectId],
          });
        }
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateBizMeeting = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  const companyId = getCompanyId();

  return useMutation(
    (vars: { id: number; payload: UpdateBizMeetingPayload }) =>
      axios({
        method: "put",
        url: `${SERVICES.BIZ_MEETINGS}/${vars.id}`,
        data: buildBizMeetingFormData(vars.payload, {
          includeBizProjectId: false,
        }),
      }),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            SERVICES.BIZ_MEETINGS,
            "detail",
            uid,
            companyId,
            variables.id,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [SERVICES.BIZ_MEETINGS, uid, companyId],
          exact: false,
        });
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "保存しました" });
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useUpdateBizMeetingStatus = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  const companyId = getCompanyId();

  return useMutation(
    ({
      id,
      status,
    }: {
      id: number;
      status: "not_started" | "in_progress" | "completed";
    }) => {
      const formData = new FormData();
      formData.append("status", status);
      return axios({
        method: "put",
        url: `${SERVICES.BIZ_MEETINGS}/${id}/status`,
        data: formData,
      });
    },
    {
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries({
          queryKey: [SERVICES.BIZ_MEETINGS, uid, companyId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [SERVICES.BIZ_MEETINGS, "detail", uid, companyId, vars.id],
        });
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        onSuccess?.();
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export const useDeleteBizMeeting = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  const companyId = getCompanyId();

  return useMutation(
    (id: number) =>
      axios({
        method: "delete",
        url: `${SERVICES.BIZ_MEETINGS}/${id}`,
      }),
    {
      onSuccess: (data, id) => {
        queryClient.invalidateQueries({
          queryKey: [SERVICES.BIZ_MEETINGS, uid, companyId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [SERVICES.BIZ_MEETINGS, "detail", uid, companyId, id],
        });
        debouncedInvalidateQueries(queryClient, [QUERY_NAME.BIZ_PROJECT, uid]);
        notification.success({ message: "削除しました" });
        onSuccess?.(data, id);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

export interface BizMeetingItem {
  id: number;
  biz_project_id: number;
  company_id?: number;
  name: string;
  status?: "not_started" | "in_progress" | "completed";
  time_meeting: string;
  meeting_day?: string | null;
  worker_ids?: number[];
  user_ids?: number[];
  can_edit_or_destroy?: boolean;
  /** true のときのみ 進捗会議設定の編集・削除ボタンを有効化 */
  meeting_button_enabled?: boolean;
  repetition_meeting: RepetitionMeetingApi;
  day_of_week?: string | null;
  week_of_month?: string | null;
  custom_interval?: number | null;
  custom_unit?: "week" | "month" | null;
  report_form?: boolean;
  report_days_before?: "one_day_before" | "two_days_before" | string | null;
  report_hours?: string | null;
  report_delayed_tasks: boolean;
  report_in_progress_tasks: boolean;
  report_new_tasks: boolean;
  report_new_tasks_target_period?: number | null;
  report_pic_announcement: boolean;
  report_forms?: ReportForm[];
  report_tasks?: ReportTask[];
  report_start: string;
}

const normalizeMeetingTimeToLocalOffset = (
  item: BizMeetingItem,
): BizMeetingItem => {
  const raw = item.time_meeting;
  if (typeof raw !== "string" || raw.trim() === "") return item;
  const m = moment.parseZone(raw);
  if (!m.isValid()) return item;
  return {
    ...item,
    // API returns +09:00; normalize to current machine local offset.
    time_meeting: m.local().format("YYYY-MM-DDTHH:mm:ssZ"),
  };
};

const normalizeBizMeetingsList = (payload: unknown): BizMeetingItem[] => {
  if (Array.isArray(payload))
    return (payload as BizMeetingItem[]).map(normalizeMeetingTimeToLocalOffset);
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return (obj.data as BizMeetingItem[]).map(
        normalizeMeetingTimeToLocalOffset,
      );
    }
    if (obj.data && typeof obj.data === "object") {
      return [
        normalizeMeetingTimeToLocalOffset(
          obj.data as unknown as BizMeetingItem,
        ),
      ];
    }
    return [
      normalizeMeetingTimeToLocalOffset(obj as unknown as BizMeetingItem),
    ];
  }
  return [];
};

const normalizeBizMeetingDetail = (
  payload: unknown,
): BizMeetingItem | undefined => {
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    if (obj.data && typeof obj.data === "object") {
      return normalizeMeetingTimeToLocalOffset(
        obj.data as unknown as BizMeetingItem,
      );
    }
    return normalizeMeetingTimeToLocalOffset(obj as unknown as BizMeetingItem);
  }
  return undefined;
};

export type UseFetchBizMeetingsOptions = {
  /**
   * When false, the query does not run on mount — use `refetch()` (e.g. Present Mode open).
   * Default true when `biz_project_id` is valid (existing screens keep auto-load).
   */
  enabled?: boolean;
};

export const useFetchBizMeetings = (
  biz_project_id?: number,
  options?: UseFetchBizMeetingsOptions,
) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const autoEnabled = options?.enabled ?? true;

  return useQuery(
    [SERVICES.BIZ_MEETINGS, uid, companyId, biz_project_id],
    () =>
      axios({
        method: "get",
        url: SERVICES.BIZ_MEETINGS_LIST,
        params: { biz_project_id },
      }).then((res) => normalizeBizMeetingsList(res.data)),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: Number.isFinite(biz_project_id) && autoEnabled,
    },
  );
};

const normalizeBizMeetingsListWithTotal = (
  payload: any,
): { list: BizMeetingItem[]; total: number } => {
  if (Array.isArray(payload?.data)) {
    const list = (payload?.data as BizMeetingItem[]).map(
      normalizeMeetingTimeToLocalOffset,
    );
    return {
      list,
      total: payload?.meta?.total_count ?? 0,
    };
  }
  return { list: [], total: 0 };
};

export type BizMeetingListParams = {
  biz_project_id?: number;
  status?: Array<"not_started" | "in_progress" | "completed">;
  report_form_status?: Array<
    "not_entered" | "save_as_draft" | "submitted" | "approved"
  >;
  page?: number;
  per_page?: number;
  offset?: number;
  worker_id?: number;
  active_filters?: {
    name?: string | boolean | string[];
    time_meeting?: string | boolean | string[];
    status?: string | boolean | string[];
    report_form_status?: string | boolean | string[];
  };
  sort?: {
    name?: string;
    time_meeting?: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
  };
  search?: Record<string, string>;
};

const buildActiveFiltersParams = (
  activeFilters?: BizMeetingListParams["active_filters"],
): Record<string, any> => {
  if (!activeFilters) return {};
  const normalized: Record<string, any> = {};
  const newActiveFilters = {
    ...activeFilters,
    time_meeting:
      activeFilters.time_meeting &&
      (activeFilters.time_meeting as string[])?.map((val) => {
        if (typeof val !== "string") return val;

        return val
          .replace("年", "-")
          .replace("月", "-")
          .replace("日", "")
          .replace(/-$/, "");
      }),
  };
  Object.entries(newActiveFilters).forEach(([key, value]) => {
    if (value === undefined) return;
    const paramKey = Array.isArray(value)
      ? `active_filters[${key}][]`
      : `active_filters[${key}]`;
    normalized[paramKey] = value;
  });
  return normalized;
};

export const useFetchBizMeetingList = (
  params: BizMeetingListParams,
  options?: { enabled?: boolean },
) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const {
    biz_project_id,
    page,
    per_page,
    offset,
    worker_id,
    active_filters,
    sort,
    search,
  } = params;

  return useQuery(
    [SERVICES.BIZ_MEETINGS, "list", uid, companyId, params],
    () =>
      axios({
        method: "get",
        url: SERVICES.BIZ_MEETINGS,
        params: {
          biz_project_id,
          page,
          per_page,
          offset,
          worker_id,
          ...buildActiveFiltersParams(active_filters),
          sort,
          search,
        },
      }).then((res) => normalizeBizMeetingsListWithTotal(res.data)),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      keepPreviousData: true,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: Number.isFinite(biz_project_id) && (options?.enabled ?? true),
    },
  );
};

export const useFetchBizMeeting = (id?: number | null) => {
  const uid = getUid();
  const companyId = getCompanyId();

  return useQuery(
    [SERVICES.BIZ_MEETINGS, "detail", uid, companyId, id],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.BIZ_MEETINGS}/${id}`,
      }).then((res) => normalizeBizMeetingDetail(res.data) as BizMeetingItem),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: Number.isFinite(id),
    },
  );
};

// ============================================
// Utility: Generate status key from name
// ============================================

/**
 * Generate a unique key from status name
 * - Keeps Japanese characters as-is
 * - Converts spaces to underscores
 * - Removes special characters
 * - Converts to lowercase for ASCII characters
 * - Appends timestamp to ensure uniqueness
 */
export const generateStatusKey = (name: string): string => {
  const baseKey = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf]/g, "");

  const timestamp = Date.now().toString(36);
  return `${baseKey}_${timestamp}`;
};

// ============================================
// Setting Members CRUD hooks
// ============================================

export interface OrgNodeEntry {
  id: number;
  children?: number[];
}

/**
 * setting_members[].role — permission the project owner assigns to each row's worker
 * (read_only | read_and_update). Distinct from the logged-in user's own project role on project detail.
 */
export type SettingMemberRole = "read_only" | "read_and_update";

export type SettingMemberWorker = {
  id: number;
  worker_no?: string | null;
  /** New setting_members payload */
  worker_name?: string | null;
  /** Legacy / project detail payload */
  full_name?: string | null;
  email?: string | null;
};

export interface SettingMember {
  id?: number;
  org_node_ids: OrgNodeEntry[];
  position_ids: string;
  worker_ids: string;
  worker_names?: string[];
  worker_emails?: (string | null)[];
  role_project: string;
  role?: SettingMemberRole;
  company_id: number;
  biz_project_id: number | null | undefined;
  _destroy?: boolean;
  /** Same on every item for a project — pin to top of assignee modal */
  owner_workers?: SettingMemberWorker[];
  admin_in_company_workers?: SettingMemberWorker[];
}

export const useFetchSettingMembers = (biz_project_id?: number) => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.SETTING_MEMBERS, companyId, biz_project_id],
    () =>
      axios({
        method: "get",
        url: SERVICES.SETTING_MEMBERS,
        params: {
          company_id: companyId,
          ...(biz_project_id != null && { biz_project_id }),
        },
      }).then((res) => res.data as SettingMember[]),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!companyId,
    },
  );
};

export type OwnerAndAdminWorkersResponse = {
  owner_workers: SettingMemberWorker[];
  admin_in_company_workers: SettingMemberWorker[];
};

/** GET /setting_members/owner_and_admin_workers */
export const useFetchOwnerAndAdminWorkers = (biz_project_id?: number) => {
  const companyId = getCompanyId();

  return useQuery(
    [
      QUERY_NAME.SETTING_MEMBERS_OWNER_AND_ADMIN_WORKERS,
      companyId,
      biz_project_id,
    ],
    () =>
      axios({
        method: "get",
        url: SERVICES.SETTING_MEMBERS_OWNER_AND_ADMIN_WORKERS,
        params: {
          company_id: companyId,
          ...(biz_project_id != null && { biz_project_id }),
        },
      }).then((res) => res.data as OwnerAndAdminWorkersResponse),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!companyId && biz_project_id != null,
    },
  );
};

// Fetch ALL setting members for a company (no biz_project_id filter)
// Endpoint: GET SERVICES.SETTING_MEMBERS_ALL (api/v1/setting_members/all)
export const useFetchAllSettingMembers = () => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.SETTING_MEMBERS, "all", companyId],
    () =>
      axios({
        method: "get",
        url: SERVICES.SETTING_MEMBERS_ALL,
        params: { company_id: companyId },
      }).then((res) => res.data as SettingMember[]),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!companyId,
    },
  );
};

/** @deprecated use ReportTask */
export type ReportFormTask = ReportTask;

/** @deprecated use ReportForm */
export type ReportFormData = ReportForm;

export const useFetchReportForm = (id?: number | null) => {
  return useQuery(
    [QUERY_NAME.REPORT_FORM, id],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.REPORT_FORMS}/${id}`,
      }).then((res) => {
        const d = res.data;
        return (d?.data ?? d) as ReportFormData;
      }),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: Number.isFinite(id) && (id ?? 0) > 0,
    },
  );
};

export interface WorkerWithoutCompanyEmail {
  id?: number;
  name: string;
}

export type ValidateWorkerEmailResult =
  | { valid: true }
  | { valid: false; workers: WorkerWithoutCompanyEmail[] };

const getWorkerDisplayName = (item: unknown): string => {
  if (typeof item === "string") return item.trim();
  if (item == null || typeof item !== "object") return "";
  const worker = item as Record<string, unknown>;
  if (typeof worker.full_name === "string" && worker.full_name.trim()) {
    return worker.full_name.trim();
  }
  if (typeof worker.name === "string" && worker.name.trim()) {
    return worker.name.trim();
  }
  if (typeof worker.worker_name === "string" && worker.worker_name.trim()) {
    return worker.worker_name.trim();
  }
  const lastName =
    typeof worker.last_name === "string" ? worker.last_name.trim() : "";
  const firstName =
    typeof worker.first_name === "string" ? worker.first_name.trim() : "";
  const combined = `${lastName} ${firstName}`.trim();
  if (combined) return combined;
  if (worker.id != null && String(worker.id).trim()) return String(worker.id);
  return "";
};

const toWorkerWithoutCompanyEmail = (
  item: unknown,
): WorkerWithoutCompanyEmail => {
  const id =
    item != null && typeof item === "object" && "id" in item
      ? Number((item as { id: unknown }).id)
      : undefined;
  return {
    ...(Number.isFinite(id) ? { id } : {}),
    name: getWorkerDisplayName(item),
  };
};

const unwrapValidatePayload = (
  data: unknown,
): Record<string, unknown> | null => {
  let current: unknown = data;
  if (typeof current === "string") {
    try {
      current = JSON.parse(current);
    } catch {
      return null;
    }
  }
  if (
    current == null ||
    typeof current !== "object" ||
    Array.isArray(current)
  ) {
    return null;
  }

  const obj = current as Record<string, unknown>;
  const inner = obj.data;
  if (
    inner != null &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    ("valid" in inner || "success" in inner || "workers_without_email" in inner)
  ) {
    return inner as Record<string, unknown>;
  }
  return obj;
};

const isValidFalse = (value: unknown) => value === false || value === "false";

const parseValidateWorkerEmailResponse = (
  data: unknown,
): ValidateWorkerEmailResult => {
  const obj = unwrapValidatePayload(data);
  if (!obj) return { valid: true };

  const rawList = obj.workers_without_email;
  const workers = Array.isArray(rawList)
    ? rawList.map(toWorkerWithoutCompanyEmail).filter((worker) => worker.name)
    : [];
  const hasWorkersWithoutEmail = Array.isArray(rawList) && rawList.length > 0;

  if (isValidFalse(obj.valid) || hasWorkersWithoutEmail) {
    return { valid: false, workers };
  }

  return { valid: true };
};

export const useValidateSettingMemberWorkerEmail = () => {
  return useMutation(
    async (params: {
      biz_project_id: number;
      worker_ids: Array<string | number>;
    }) => {
      try {
        const res = await axios({
          method: "get",
          url: SERVICES.SETTING_MEMBERS_VALIDATE_WORKER_EMAIL,
          params: {
            biz_project_id: params.biz_project_id,
            worker_ids: params.worker_ids.join(","),
          },
          // Backend may return this payload with a non-2xx HTTP status.
          validateStatus: (status) => status < 500,
        });
        return parseValidateWorkerEmailResponse(res.data);
      } catch (e: any) {
        const parsed = parseValidateWorkerEmailResponse(e?.response?.data);
        if (!parsed.valid) return parsed;
        throw e;
      }
    },
    {
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(
            e?.response?.data?.message || e?.response?.data?.error,
          ),
        });
      },
    },
  );
};

export const useCreateSettingMembers = () => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    (payload: { setting_members: SettingMember[]; biz_project_id?: number }) =>
      axios({
        method: "post",
        url: SERVICES.SETTING_MEMBERS,
        params:
          payload.biz_project_id != null
            ? { biz_project_id: payload.biz_project_id }
            : undefined,
        data: { setting_members: payload.setting_members },
      }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_NAME.SETTING_MEMBERS],
        });

        if (variables.biz_project_id != null) {
          debouncedInvalidateQueries(queryClient, [
            QUERY_NAME.BIZ_PROJECT,
            uid,
            String(variables.biz_project_id),
          ]);
        }
        // Refresh assignee options used by gantt filters (list + project detail).
        debouncedInvalidateQueries(queryClient, [
          QUERY_NAME.BIZ_PROJECT_ASSIGNEES,
          uid,
        ]);

        notification.success({ message: "保存しました" });
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.message),
        });
      },
    },
  );
};

export interface IBulkUpdateReportTask {
  id: number;
  reason?: string;
  countermeasure?: string;
  on_schedule?: string;
  status?: string;
  team_message?: string;
  pm_memo?: string;
  is_present_mode?: boolean;
}

export const useUpdateReportFormStatus = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      status,
      is_present_mode,
    }: {
      id: number;
      status: "save_as_draft" | "submitted" | "approved";
      is_present_mode?: boolean;
    }) => {
      const formData = new FormData();
      formData.append("status", status);
      if (is_present_mode !== undefined) {
        formData.append("is_present_mode", String(is_present_mode));
      }
      return axios({
        method: "put",
        url: `${SERVICES.REPORT_FORMS}/${id}`,
        data: formData,
      });
    },
    {
      onSuccess: () => {
        debouncedInvalidateQueries(queryClient, [
          SERVICES.BIZ_MEETINGS,
          "list",
        ]);
        onSuccess?.();
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.msg),
        });
      },
    },
  );
};

// Comments
export const useAddComment = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();

  return useMutation(
    ({
      payload,
      showNotification = true,
    }: {
      payload: any;
      showNotification?: boolean;
    }) =>
      axios({
        method: "post",
        url: "/comments",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess: (data, variables) => {
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        if (variables.showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.message),
        });
      },
    },
  );
};

export const useFetchComments = (filterParams: { biz_project_id?: number }) => {
  const uid = getUid();

  return useQuery(
    [QUERY_NAME.COMMENTS, uid, filterParams?.biz_project_id],
    () =>
      axios({
        method: "get",
        url: "/comments",
        params: filterParams,
      }).then((res) => res.data as any),
    {
      cacheTime: 60000,
      staleTime: 0,
      refetchOnMount: "always",
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!getAccessToken(),
    },
  );
};

export const useDeleteComment = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({ id }: any) =>
      axios({
        method: "delete",
        url: `/comments/${id}`,
      }),
    {
      onSuccess() {
        onSuccess?.();
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        console.log(e);
      },
    },
  );
};

/** POST /comments/bulk_delete — hard-delete an entire comment thread. */
export const useBulkDeleteComments = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({ ids }: { ids: Array<string | number> }) =>
      axios({
        method: "post",
        url: "/comments/bulk_delete",
        data: { ids: ids.map((id) => Number(id)).filter(Number.isFinite) },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess() {
        onSuccess?.();
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.message),
        });
      },
    },
  );
};

/** POST /comments/hide — soft-hide a single comment (formData: id, worker_id). */
export const useHideComment = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({
      id,
      worker_id,
    }: {
      id: string | number;
      worker_id?: string | number | null;
    }) => {
      const form = new FormData();
      form.append("id", String(id));
      if (worker_id != null && String(worker_id).trim() !== "") {
        form.append("worker_id", String(worker_id));
      }
      return axios({
        method: "post",
        url: "/comments/hide",
        data: form,
      });
    },
    {
      onSuccess() {
        onSuccess?.();
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.message),
        });
      },
    },
  );
};

/** PUT /comments/:id — edit comment body (and optional metadata). */
export const useUpdateComment = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({
      id,
      comment,
      status,
      cell_date,
      cell_offset_ms,
      size_comment,
      read_ids,
      showNotification = true,
    }: {
      id: string | number;
      comment?: string;
      status?: string;
      cell_date?: string;
      cell_offset_ms?: number | string;
      size_comment?: number;
      read_ids?: number[];
      showNotification?: boolean;
    }) => {
      const form = new FormData();
      if (comment !== undefined) form.append("comment", comment);
      if (status !== undefined) form.append("status", status);
      if (cell_date !== undefined) form.append("cell_date", cell_date);
      if (cell_offset_ms !== undefined) {
        form.append("cell_offset_ms", String(cell_offset_ms));
      }
      if (size_comment !== undefined) {
        form.append("size_comment", String(size_comment));
      }
      if (Array.isArray(read_ids)) {
        read_ids.forEach((rid) => form.append("read_ids[]", String(rid)));
      }
      return axios({
        method: "put",
        url: `/comments/${id}`,
        data: form,
      });
    },
    {
      onSuccess(data, variables) {
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        if (variables.showNotification !== false) {
          notification.success({ message: "保存しました" });
        }
        onSuccess?.(data, variables);
      },
      onError(e: any) {
        notification["error"]({
          message: handleMessageError(e?.response?.data?.message),
        });
      },
    },
  );
};

export const useUpdateCommentStatus = ({ onSuccess }: any = {}) => {
  const queryClient = useQueryClient();
  const uid = getUid();
  return useMutation(
    ({
      id,
      status,
      read_ids,
      size_comment,
      cell_date,
      cell_offset_ms,
      comment,
    }: any) => {
      const data: any = {};
      if (status !== undefined) data.status = status;
      if (read_ids?.length > 0) data.read_ids = read_ids;
      if (size_comment !== undefined) data.size_comment = size_comment;
      if (cell_date !== undefined) data.cell_date = cell_date;
      if (cell_offset_ms !== undefined) data.cell_offset_ms = cell_offset_ms;
      if (comment !== undefined) data.comment = comment;
      return axios({
        method: "put",
        url: `/comments/${id}/update_status`,
        data,
      });
    },
    {
      onSuccess(data, variables) {
        debouncedInvalidateQueries(
          queryClient,
          [QUERY_NAME.COMMENTS, uid],
          100,
          "none",
        );
        onSuccess?.(data, variables);
      },
      onError(error: any) {
        console.log(error);
      },
    },
  );
};

export const useUpdateReportTasksBulk = ({
  onSuccess,
  notifySuccess = true,
}: { onSuccess?: () => void; notifySuccess?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    (reportTasks: IBulkUpdateReportTask[]) => {
      const isPresentMode = reportTasks.some(
        (task) => task.is_present_mode === true,
      );
      const payload = reportTasks.map((task) => ({
        id: task.id,
        reason: task.reason ?? "",
        countermeasure: task.countermeasure ?? "",
        ...(task.on_schedule ? { on_schedule: task.on_schedule } : {}),
        ...(task.status ? { status: task.status } : {}),
        ...(task.team_message ? { team_message: task.team_message } : {}),
        ...(task.pm_memo !== undefined ? { pm_memo: task.pm_memo } : {}),
      }));
      return axios({
        method: "put",
        url: `${SERVICES.REPORT_TASKS}/bulk_update`,
        data: {
          report_tasks: payload,
          ...(isPresentMode ? { is_present_mode: true } : {}),
        },
      });
    },
    {
      onSuccess: () => {
        debouncedInvalidateQueries(queryClient, [
          SERVICES.BIZ_MEETINGS,
          "list",
        ]);
        if (notifySuccess) notification.success({ message: "送信しました" });
        onSuccess?.();
      },
      onError(e: any) {
        const msg = e?.response?.data?.msg;
        if (
          msg ===
            "会議を開始する時間です。フォームは自動的にロックされ、報告されます。" ||
          msg === "送信期限が過ぎています"
        ) {
          debouncedInvalidateQueries(queryClient, [
            SERVICES.BIZ_MEETINGS,
            "list",
          ]);
        }
        notification["error"]({
          message: handleMessageError(msg),
        });
      },
    },
  );
};

//get filter values

type FilterValuesParams = {
  biz_project_id?: number;
  field_names?: Array<
    "name" | "time_meeting" | "status" | "report_form_status"
  >;
  active_filters?: { [key: string]: string | boolean | string[] };
};

type FilterValuesResponse = {
  success?: boolean;
  filter_options?: {
    status?: Array<"not_started" | "in_progress" | "completed">;
    time_meeting?: Array<{
      value: string;
      label: string;
      year: number;
      month: number;
    }>;
    report_form_status?: Array<
      "not_entered" | "save_as_draft" | "submitted" | "approved"
    >;
    name?: Array<string>;
  };
  active_filters?: { [key: string]: string | boolean | string[] };
};

export const useGetFilterValues = ({
  params,
  enabled = true,
}: {
  params: FilterValuesParams;
  enabled?: boolean;
}) => {
  const newParams = {
    ...params,
    active_filters: {
      ...params.active_filters,
      time_meeting:
        params.active_filters?.time_meeting &&
        (params.active_filters.time_meeting as string[])?.map((val) => {
          if (typeof val !== "string") return val;

          return val
            .replace("年", "-")
            .replace("月", "-")
            .replace("日", "")
            .replace(/-$/, "");
        }),
    },
  };
  return useQuery(
    [SERVICES.BIZ_MEETINGS, "filter_options", newParams],
    () =>
      axios({
        method: "POST",
        url: `${SERVICES.BIZ_MEETINGS}/filter_options`,
        data: newParams,
      }).then((res) => res.data as FilterValuesResponse),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      keepPreviousData: true,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: enabled,
    },
  );
};
